import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import "@/App.css";

import { AuthProvider } from "./lib/auth";

import Homepage from "./pages/Homepage";
import { Login, Signup } from "./pages/Auth";
import AuthCallback from "./pages/AuthCallback";
import Onboarding from "./pages/Onboarding";
import ChoosePlanPage from "./pages/ChoosePlanPage";
import PublicSite from "./pages/PublicSite";
import PublicRsvp from "./pages/PublicRsvp";
import PublicGuestUpload from "./pages/PublicGuestUpload";
import PodcastPage from "./pages/PodcastPage";
import PricingPage from "./pages/PricingPage";
import { PrivacyPage, TermsPage } from "./pages/LegalPages";
import AudienceLanding from "./pages/AudienceLanding";
import AdminHomepagePage from "./pages/dashboard/AdminHomepagePage";
import AdminInsightsPage from "./pages/dashboard/AdminInsightsPage";
import ReceptionModePage from "./pages/dashboard/ReceptionModePage";
import ReceptionLivePage from "./pages/ReceptionLivePage";
import TableHubPage from "./pages/TableHubPage";
import GuestMessagesPage from "./pages/dashboard/GuestMessagesPage";
import VideosPage from "./pages/dashboard/VideosPage";
import AnimatedInvitesPage from "./pages/dashboard/AnimatedInvitesPage";
import PublicInvitePage from "./pages/PublicInvitePage";
import SocialInvitePage from "./pages/SocialInvitePage";
import ThanksListenPage from "./pages/ThanksListenPage";
import PremiumWeddingPage from "./pages/PremiumWeddingPage";
import SocialHubPage from "./pages/SocialHubPage";
import AiPlannerPage from "./pages/dashboard/AiPlannerPage";
import { GuestLogin, GuestSignup, GuestForgot, GuestReset } from "./pages/guest/GuestAuthPages";
import { GuestHome, GuestWedding } from "./pages/guest/GuestPortalPages";
import BillingSuccess from "./pages/BillingSuccess";
import NotFoundPage from "./pages/NotFoundPage";

import DashboardLayout from "./pages/dashboard/DashboardLayout";
import DashboardHome from "./pages/dashboard/DashboardHome";
import GuestsPage from "./pages/dashboard/GuestsPage";
import MemoriesPage from "./pages/dashboard/MemoriesPage";
import PayoutsPage from "./pages/dashboard/PayoutsPage";
import ThankYouPage from "./pages/dashboard/ThankYouPage";
import MarketplaceDashboard, { MarketSellPage } from "./pages/dashboard/MarketplaceDashboard";
import MarketHome from "./pages/market/MarketHome";
import MarketItem from "./pages/market/MarketItem";
import MarketInbox from "./pages/market/MarketInbox";
import MarketOrderSuccess from "./pages/market/MarketOrderSuccess";
import MarketCart from "./pages/market/MarketCart";
import CallCenter, { CallCenterPopout } from "./pages/sales/CallCenter";
import RepPortal from "./pages/sales/RepPortal";
import RepInvite from "./pages/sales/RepInvite";
import SendToTeam from "./pages/SendToTeam";
import MeetingCalendar from "./pages/sales/MeetingCalendar";
import VendorIntel from "./pages/sales/VendorIntel";
import LeadPreviewPage from "./pages/LeadPreviewPage";
import ConferenceCenterPage from "./pages/dashboard/ConferenceCenterPage";
import OnboardingPage from "./pages/dashboard/OnboardingPage";
import SalesAdminPage from "./pages/sales/SalesAdminPage";
import { QuotePage, GiftCardsPage, GiftCardSuccess, GiftCardView, MeetPage, AuditPage, JoinVendorPage, ProfilePage as MyProfilePage } from "./pages/sales/PublicSalesPages";
import AccountOrdersPage from "./pages/AccountOrdersPage";
import RegistryGiftSuccess from "./pages/RegistryGiftSuccess";
import InvoicesPage from "./pages/dashboard/InvoicesPage";
import MarketSaved from "./pages/market/MarketSaved";
import WebsiteBuilderPage from "./pages/dashboard/WebsiteBuilderPage";
import InvitationsPage from "./pages/dashboard/InvitationsPage";
import RsvpsPage from "./pages/dashboard/RsvpsPage";
import SettingsPage from "./pages/dashboard/SettingsPage";
import DressTuxPage from "./pages/dashboard/DressTuxPage";
import BudgetPage from "./pages/dashboard/BudgetPage";
import RegistryPage from "./pages/dashboard/RegistryPage";
import TimelinePage from "./pages/dashboard/TimelinePage";
import VowStudioPage from "./pages/dashboard/VowStudioPage";
import SeatingChartPage from "./pages/dashboard/SeatingChartPage";
import SessionsPage from "./pages/dashboard/SessionsPage";
import GoLivePage from "./pages/dashboard/GoLivePage";
import ExplorePage from "./pages/ExplorePage";
import { CommunityFeedPage, ProfilePage, DirectoryPage } from "./pages/CommunityPages";
import MessagesPage from "./pages/MessagesPage";
import AdminCommunityPage from "./pages/dashboard/AdminCommunityPage";
import ContactsPage from "./pages/dashboard/ContactsPage";
import PartyGamesPage from "./pages/dashboard/PartyGamesPage";
import ClientPlanPage from "./pages/dashboard/ClientPlanPage";
import PartyPlayPage from "./pages/PartyPlayPage";
import SetupFeePage from "./pages/SetupFeePage";
import MyReportsPage from "./pages/dashboard/MyReportsPage";
import PlatformReportsPage from "./pages/dashboard/PlatformReportsPage";
import AdminProfilesPage from "./pages/dashboard/AdminProfilesPage";
import MobileTabBar from "./components/MobileTabBar";
import InstallPrompt from "./components/InstallPrompt";
import ReferralsPage from "./pages/dashboard/ReferralsPage";
import VendorProfileEditor from "./pages/dashboard/VendorProfileEditor";
import VendorInboxPage from "./pages/dashboard/VendorInboxPage";
import ClaimLinkPage from "./pages/ClaimLinkPage";
import { CareersList, CareerJob } from "./pages/careers/Careers";
import CareerPortal from "./pages/careers/CareerPortal";
import InterviewRoom from "./pages/careers/InterviewRoom";
import SeoCenterPage from "./pages/SeoCenterPage";
import { SiteTags } from "./components/SiteTags";
import WeddingsLanding from "./pages/WeddingsLanding";
import BillingPage from "./pages/dashboard/BillingPage";
import AdminPage from "./pages/AdminPage";
import PlannerDashboard from "./pages/PlannerDashboard";
import HallOfFamePage from "./pages/HallOfFamePage";
import WatchOnboardingPage from "./pages/WatchOnboardingPage";
import SessionRoomPage from "./pages/SessionRoomPage";
import { LiveIndex, LiveRoom } from "./pages/LivePages";
import VendorSocialPage, { VendorRedirect } from "./pages/VendorSocialPage";
import { PlannersDirectory, PlannerPublicPage, PlannerListPage } from "./pages/PlannerPages";
import DocViewPage from "./pages/DocViewPage";
import TestimonialRecordPage from "./pages/TestimonialRecordPage";
import VerifyCertificatePage from "./pages/VerifyCertificatePage";
import AdminGoLivePage from "./pages/dashboard/AdminGoLivePage";
import PlanningHubPage from "./pages/dashboard/PlanningHubPage";
import SavedVendorsPage from "./pages/dashboard/SavedVendorsPage";
import PlannerPageEditor from "./pages/dashboard/PlannerPageEditor";
import PreferredVendorsPage from "./pages/dashboard/PreferredVendorsPage";
import EstimatesPage from "./pages/dashboard/EstimatesPage";
import SocialCenterPage from "./pages/SocialCenterPage";
import JoinLanding from "./pages/JoinLanding";
import TvMeetingPage from "./pages/TvMeetingPage";
import CouplesClubPage, { MyClubPage } from "./pages/CouplesClubPage";
import SiteBanner from "./components/SiteBanner";
import { ContestBanner } from "./components/contest/ContestBanner";
import ContestPage, { ContestVotePage } from "./pages/ContestPage";
import InviteTrendPage from "./pages/InviteTrendPage";
import DemoFeaturePage from "./pages/DemoFeaturePage";
import { ConciergePage, QuizPage, LoveStoryPage, AnniversaryPage, AlbumPage } from "./pages/dashboard/ExtrasPages";
import { LockedInHub, LockedInAnswerPage, LockedInMatchPage, LockedInGameCreate, LockedInGameHost } from "./pages/dashboard/LockedInPages";
import { CompatibilityPage, PublicAnnouncementsPage, AnnouncementsManagerPage } from "./pages/dashboard/SharePages";
import PublicGamePage from "./pages/PublicGamePage";
import ReceptionScreen from "./pages/ReceptionScreen";
import PartyScreen from "./pages/PartyScreen";
import NewsWall from "./pages/NewsWall";
import WaiverPage, { WaiverGate } from "./pages/WaiverPage";
import { TourGuide } from "./components/TourGuide";
import NewsFeed from "./pages/NewsFeed";
import WeddingSocial from "./pages/WeddingSocial";
import VideoNotePage from "./pages/VideoNotePage";
import PublicSeating from "./pages/PublicSeating";
import AttirePayPage from "./pages/AttirePayPage";
import PremierePage from "./pages/PremierePage";
import { PublicTravel, SharedJoin, SharedPlanView } from "./pages/PlanningPublic";
import QuotesPage from "./pages/dashboard/QuotesPage";
import TravelPage from "./pages/dashboard/TravelPage";
import TvPage from "./pages/TvPage";
import RegistryCardPrint from "./pages/RegistryCardPrint";
import ReelsPage from "./pages/dashboard/ReelsPage";
import BlessingsPage from "./pages/dashboard/BlessingsPage";
import ShareStudioPage from "./pages/dashboard/ShareStudioPage";
import EventsPage from "./pages/dashboard/EventsPage";
import { ChallengeHubPage, ChallengeRoomPage } from "./pages/dashboard/CoupleChallengePages";
import {
  AdminOverviewPage, AdminWeddingsPage, AdminUsersPage, AdminModerationPage,
  AdminVendorsPage, AdminRevenuePage, AdminReportsPage, AdminBroadcastPage,
  AdminAuditLogPage,
} from "./pages/dashboard/AdminConsolePages";
import AdminBlogPage from "./pages/dashboard/AdminBlogPage";
import {
  GuestProfilePage, WeddingPartyPage, PublicFeedTab, PublicFeedPage,
} from "./pages/dashboard/WeddingPartyPages";
import PartyMemberPage from "./pages/PartyMemberPage";
import RosterGalleryPage from "./pages/RosterGalleryPage";
import ManagePartyMessagesPage from "./pages/dashboard/ManagePartyMessagesPage";
import LocationPage, { LocationsIndexPage } from "./pages/LocationPage";
import { BlogIndexPage, BlogPostPage } from "./pages/BlogPages";
import AgreementSignPage from "./pages/AgreementSignPage";
import PointsWalletPage from "./pages/dashboard/PointsWalletPage";
import MyAgreementsPage from "./pages/dashboard/MyAgreementsPage";
import AdminAgreementsPage from "./pages/dashboard/AdminAgreementsPage";
import AdminViewsPage from "./pages/dashboard/AdminViewsPage";
import { GiftThanksPage, HighlightsPage } from "./components/live/LiveHighlights";
import { useViewAs } from "./lib/viewAs";
import AdminTherapyPage from "./pages/dashboard/AdminTherapyPage";
import AdminPodcastPage, { PodcastLiveRoomPage } from "./pages/dashboard/AdminPodcastPage";
import AdminNotificationsPage from "./pages/dashboard/AdminNotificationsPage";
import { TherapyHomePage, TherapyApplyPage, TherapyBookPage } from "./pages/TherapyPages";
import { TherapyIntakePage, TherapySessionRoomPage, TherapyFeedbackPage } from "./pages/TherapySessionPages";
import { MyTherapyPage, TherapistAvailabilityPage, TherapistNotesPage } from "./pages/dashboard/TherapyDashboardPages";
import { useAuth } from "./lib/auth";
import { Navigate } from "react-router-dom";

/**
 * Gate that ensures the user has explicitly chosen a plan before they
 * can reach onboarding. Without this, /onboarding becomes the first page
 * after login (per the user's complaint).
 */
function RequirePlan({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin" && !user.plan_selected) return <Navigate to="/choose-plan" replace />;
  return children;
}

/**
 * Dashboard gate: must have plan_selected AND has_wedding (otherwise route
 * to onboarding first).
 */
function RequireSetup({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return children; // admin bypass
  // Vendors & planners don't need a couple wedding record; bypass wedding-setup
  // gate but still require plan selection.
  if (!user.plan_selected) return <Navigate to="/choose-plan" replace />;
  // Every account pays the one-time $9.99 setup fee.
  if (!user.setup_fee_paid) return <Navigate to="/setup-fee" replace />;
  if (user.role === "vendor" || user.role === "planner") return children;
  if (!user.has_wedding && !/^\/dashboard\/(planning-hub|saved-vendors)/.test(loc.pathname)) return <Navigate to="/onboarding" replace />;
  return children;
}

// Sellers (brides & bridal stores) only need an account.
function RequireSeller({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  // Posting on the marketplace is free: any signed-in account can sell, no plan or setup fee needed.
  if (!user) { sessionStorage.setItem("lif_after_login", "/market/sell"); return <Navigate to="/signup?seller=1" replace />; }
  return children;
}

/**
 * Dashboard index switcher: admins see the AdminOverviewPage, everyone else
 * sees the couple DashboardHome.
 */
function DashboardIndexSwitcher() {
  const { user } = useAuth();
  const viewAs = useViewAs(user);
  if (user?.role === "admin" && viewAs === "admin") return <AdminOverviewPage />;
  return <DashboardHome viewAs={viewAs} />;
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  React.useEffect(() => { if (!hash) window.scrollTo(0, 0); }, [pathname, hash]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  // Process Google OAuth session_id synchronously before any other route
  if (typeof window !== "undefined" && location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  // Don't show banner inside live room, session room or guest game (immersive)
  const hideBanner = /^\/live\/|^\/session\/|^\/play\/|^\/reception\/|^\/reception-live\/|^\/invite\/|^\/s\/|^\/party\/|^\/party-screen\/|^\/tv$|^\/print\/|^\/record\/|^\/doc\/|^\/verify\//.test(location.pathname);

  const pageKey = location.pathname.startsWith("/dashboard") ? "dashboard" : location.pathname;

  return (
    <>
      <ScrollToTop />
      {!hideBanner && <SiteBanner />}
      <ContestBanner />
      <motion.div key={pageKey} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.35, ease: "easeOut" }}>
      <WaiverGate>
      <Routes>
      <Route path="/waiver" element={<WaiverPage />} />
      <Route path="/explore" element={<ExplorePage />} />
      <Route path="/party/:code" element={<PartyPlayPage />} />
      <Route path="/setup-fee" element={<SetupFeePage />} />
      <Route path="/community" element={<CommunityFeedPage />} />
      <Route path="/community/directory" element={<DirectoryPage />} />
      <Route path="/community/me" element={<ProfilePage />} />
      <Route path="/community/u/:handle" element={<ProfilePage />} />
      <Route path="/messages" element={<MessagesPage />} />
      <Route path="/messages/:threadId" element={<MessagesPage />} />
      <Route path="/" element={<Homepage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/choose-plan" element={<ChoosePlanPage />} />
      <Route path="/onboarding" element={<RequirePlan><Onboarding /></RequirePlan>} />

      <Route path="/podcast" element={<PodcastPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/for-couples" element={<AudienceLanding audience="couples" />} />
      <Route path="/for-planners" element={<AudienceLanding audience="planners" />} />
      <Route path="/for-venues" element={<AudienceLanding audience="venues" />} />
      <Route path="/billing/success" element={<BillingSuccess />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/planner" element={<PlannerDashboard />} />
      <Route path="/featured" element={<HallOfFamePage />} />
      <Route path="/club" element={<CouplesClubPage />} />
      <Route path="/session/:id" element={<SessionRoomPage />} />
      <Route path="/live" element={<LiveIndex />} />
      <Route path="/live/gift-thanks" element={<GiftThanksPage />} />
      <Route path="/live/:id/highlights" element={<HighlightsPage />} />
      <Route path="/live/:id" element={<LiveRoom />} />
      <Route path="/vendors" element={<Navigate to="/hub?tab=vendors" replace />} />
      <Route path="/vendors/:id" element={<VendorRedirect />} />
      <Route path="/hub" element={<SocialCenterPage />} />
      <Route path="/join" element={<JoinLanding variant="both" />} />
      <Route path="/join/social" element={<JoinLanding variant="social" />} />
      <Route path="/join/plans" element={<JoinLanding variant="plans" />} />
      <Route path="/v/:id" element={<VendorSocialPage />} />
      <Route path="/planners" element={<PlannersDirectory />} />
      <Route path="/planners/:slug" element={<PlannerPublicPage />} />
      <Route path="/planner-list/:id" element={<PlannerListPage />} />
      <Route path="/doc/:token" element={<DocViewPage />} />
      <Route path="/record/:token" element={<TestimonialRecordPage />} />
      <Route path="/verify/:id" element={<VerifyCertificatePage />} />
      <Route path="/claim/:token" element={<ClaimLinkPage />} />
      <Route path="/careers" element={<CareersList />} />
      <Route path="/careers/me" element={<CareerPortal />} />
      <Route path="/careers/interview/:aid" element={<InterviewRoom />} />
      <Route path="/seo-center" element={<SeoCenterPage />} />
      <Route path="/weddings/*" element={<WeddingsLanding />} />
      <Route path="/careers/:id" element={<CareerJob />} />
      <Route path="/tv/m/:code" element={<TvMeetingPage />} />

      <Route path="/demo/:feature" element={<DemoFeaturePage />} />
      <Route path="/play/:code" element={<PublicGamePage />} />
      <Route path="/reception/:code" element={<ReceptionScreen />} />
      <Route path="/party-screen/:code" element={<PartyScreen />} />
      <Route path="/tv" element={<TvPage />} />
      <Route path="/market" element={<MarketHome />} />
      <Route path="/market/item/:id" element={<MarketItem />} />
      <Route path="/market/inbox" element={<MarketInbox />} />
      <Route path="/market/saved" element={<MarketSaved />} />
      <Route path="/market/sell" element={<RequireSeller><MarketSellPage /></RequireSeller>} />
      <Route path="/market/order/success" element={<MarketOrderSuccess />} />
      <Route path="/market/cart" element={<MarketCart />} />
      <Route path="/call-center" element={<CallCenter />} />
      <Route path="/rep" element={<RepPortal />} />
      <Route path="/rep/invite/:token" element={<RepInvite />} />
      <Route path="/send/:ext" element={<SendToTeam />} />
      <Route path="/call-center/popout/:panel" element={<CallCenterPopout />} />
      <Route path="/call-center/meetings" element={<MeetingCalendar />} />
      <Route path="/admin/vendor-intelligence" element={<VendorIntel />} />
      <Route path="/admin/dialer" element={<VendorIntel />} />
      <Route path="/q/:token" element={<QuotePage />} />
      <Route path="/gift-cards" element={<GiftCardsPage />} />
      <Route path="/gift-cards/success" element={<GiftCardSuccess />} />
      <Route path="/gift-cards/card/:code" element={<GiftCardView />} />
      <Route path="/meet/:id" element={<MeetPage />} />
      <Route path="/watch/:token" element={<WatchOnboardingPage />} />
      <Route path="/audit/:token" element={<AuditPage />} />
      <Route path="/join-vendor" element={<JoinVendorPage />} />
      <Route path="/preview/:token" element={<LeadPreviewPage />} />
      <Route path="/contest" element={<ContestPage />} />
      <Route path="/invite-trend" element={<InviteTrendPage />} />
      <Route path="/contest/vote/:slug" element={<ContestVotePage />} />
      <Route path="/account/orders" element={<AccountOrdersPage />} />
      <Route path="/site/:slug/gift/success" element={<RegistryGiftSuccess />} />
      <Route path="/news-wall/:slug" element={<NewsWall />} />
      <Route path="/print/registry-card" element={<RequireSetup><RegistryCardPrint /></RequireSetup>} />
      <Route path="/announcements" element={<PublicAnnouncementsPage />} />
      <Route path="/feed" element={<PublicFeedPage />}
      />

      <Route path="/site/:slug" element={<PublicSite />} />
      <Route path="/site/:slug/party/:guestId" element={<PartyMemberPage />} />
      <Route path="/site/:slug/party/:guestId/photos" element={<RosterGalleryPage />} />
      <Route path="/site/:slug/rsvp/:guestId" element={<PublicRsvp />} />
      <Route path="/site/:slug/upload" element={<PublicGuestUpload />} />
      <Route path="/site/:slug/news" element={<NewsFeed />} />
      <Route path="/site/:slug/social" element={<WeddingSocial />} />
      <Route path="/video-note/:token" element={<VideoNotePage />} />
      <Route path="/site/:slug/seating" element={<PublicSeating />} />
      <Route path="/site/:slug/table/:tableId" element={<TableHubPage />} />
      <Route path="/reception-live/:slug" element={<ReceptionLivePage />} />
      <Route path="/guest" element={<GuestHome />} />
      <Route path="/invite/:slug/:inviteId" element={<PublicInvitePage />} />
      <Route path="/s/:token" element={<SocialInvitePage />} /> {/* also serves SEO landing pages /s/:slug (fallback inside) */}
      <Route path="/thanks/:token" element={<ThanksListenPage />} />
      <Route path="/the-2-million-wedding" element={<PremiumWeddingPage />} />
      <Route path="/social" element={<SocialHubPage />} />
      <Route path="/guest/login" element={<GuestLogin />} />
      <Route path="/guest/signup" element={<GuestSignup />} />
      <Route path="/guest/forgot" element={<GuestForgot />} />
      <Route path="/guest/reset" element={<GuestReset />} />
      <Route path="/guest/w/:weddingId" element={<GuestWedding />} />
      <Route path="/pay/attire/:token" element={<AttirePayPage />} />
      <Route path="/premiere/:slug" element={<PremierePage />} />
      <Route path="/site/:slug/travel" element={<PublicTravel />} />
      <Route path="/shared/join/:token" element={<SharedJoin />} />
      <Route path="/shared/:weddingId" element={<SharedPlanView />} />

      {/* SEO landing pages — 50 subdomain-equivalents + cities + blog */}
      <Route path="/locations" element={<LocationsIndexPage />} />
      <Route path="/locations/:city/:topic" element={<LocationPage />} />
      <Route path="/blog" element={<BlogIndexPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />

      <Route path="/giveaway/*" element={<Navigate to="/contest" replace />} />
      <Route path="/c/:slug" element={<Navigate to="/contest" replace />} />
      <Route path="/agreements/sign/:token" element={<AgreementSignPage />} />

      {/* Therapy public flows */}
      <Route path="/therapy" element={<TherapyHomePage />} />
      <Route path="/therapy/apply" element={<TherapyApplyPage />} />
      <Route path="/therapy/book/:slug" element={<TherapyBookPage />} />
      <Route path="/therapy/intake/:id" element={<TherapyIntakePage />} />
      <Route path="/therapy/session/:id" element={<TherapySessionRoomPage />} />
      <Route path="/therapy/feedback/:id" element={<TherapyFeedbackPage />} />
      <Route path="/podcast/live/:id" element={<PodcastLiveRoomPage />} />

      <Route path="/dashboard" element={<RequireSetup><DashboardLayout /></RequireSetup>}>
        <Route index element={<DashboardIndexSwitcher />} />
        <Route path="admin" element={<AdminOverviewPage />} />
        <Route path="admin/weddings" element={<AdminWeddingsPage />} />
        <Route path="admin/users" element={<AdminUsersPage />} />
        <Route path="admin/moderation" element={<AdminModerationPage />} />
        <Route path="admin/vendors" element={<AdminVendorsPage />} />
        <Route path="admin/revenue" element={<AdminRevenuePage />} />
        <Route path="admin/invoices" element={<InvoicesPage admin />} />
        <Route path="invoices" element={<InvoicesPage />} />
        <Route path="sales-admin" element={<SalesAdminPage />} />
        <Route path="profile" element={<MyProfilePage />} />
        <Route path="admin/insights" element={<AdminInsightsPage />} />
        <Route path="admin/reports" element={<AdminReportsPage />} />
        <Route path="admin/broadcast" element={<AdminBroadcastPage />} />
        <Route path="admin/blog" element={<AdminBlogPage />} />
        <Route path="admin/giveaway" element={<Navigate to="/contest" replace />} />
        <Route path="admin/agreements" element={<AdminAgreementsPage />} />
        <Route path="admin/views" element={<AdminViewsPage />} />
        <Route path="admin/therapy" element={<AdminTherapyPage />} />
        <Route path="admin/podcast" element={<AdminPodcastPage />} />
        <Route path="admin/go-live" element={<AdminGoLivePage />} />
        <Route path="admin/notifications" element={<AdminNotificationsPage />} />
        <Route path="admin/homepage" element={<AdminHomepagePage />} />
        <Route path="admin/community" element={<AdminCommunityPage />} />
        <Route path="admin/platform-reports" element={<PlatformReportsPage />} />
        <Route path="admin/profiles" element={<AdminProfilesPage />} />
        <Route path="contacts" element={<ContactsPage />} />
        <Route path="party-games" element={<PartyGamesPage />} />
        <Route path="client-plan/:weddingId" element={<ClientPlanPage />} />
        <Route path="planning-hub" element={<PlanningHubPage />} />
        <Route path="saved-vendors" element={<SavedVendorsPage />} />
        <Route path="planner-page" element={<PlannerPageEditor />} />
        <Route path="preferred-vendors" element={<PreferredVendorsPage />} />
        <Route path="estimates" element={<EstimatesPage />} />
        <Route path="reports" element={<MyReportsPage />} />
        <Route path="therapy" element={<MyTherapyPage />} />
        <Route path="conference" element={<ConferenceCenterPage />} />
        <Route path="onboarding" element={<OnboardingPage />} />
        <Route path="therapy/availability" element={<TherapistAvailabilityPage />} />
        <Route path="therapy/notes/:id" element={<TherapistNotesPage />} />
        <Route path="admin/audit" element={<AdminAuditLogPage />} />
        <Route path="website" element={<WebsiteBuilderPage />} />
        <Route path="guests" element={<GuestsPage />} />
        <Route path="guests/:guestId" element={<GuestProfilePage />} />
        <Route path="wedding-party" element={<WeddingPartyPage />} />
        <Route path="party-messages" element={<ManagePartyMessagesPage />} />
        <Route path="memories" element={<MemoriesPage />} />
        <Route path="payouts" element={<PayoutsPage />} />
        <Route path="quotes" element={<QuotesPage />} />
        <Route path="travel" element={<TravelPage />} />
        <Route path="thank-yous" element={<ThankYouPage />} />
        <Route path="marketplace" element={<MarketplaceDashboard />} />
        <Route path="reels" element={<ReelsPage />} />
        <Route path="blessings" element={<BlessingsPage />} />
        <Route path="share-studio" element={<ShareStudioPage />} />
        <Route path="events" element={<EventsPage />} />
        <Route path="locked-in/challenges" element={<ChallengeHubPage />} />
        <Route path="locked-in/challenges/:id" element={<ChallengeRoomPage />} />
        <Route path="invitations" element={<InvitationsPage />} />
        <Route path="rsvps" element={<RsvpsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="dress-tux" element={<DressTuxPage />} />
        <Route path="my-campaign" element={<Navigate to="/contest" replace />} />
        <Route path="wallet" element={<PointsWalletPage />} />
        <Route path="my-agreements" element={<MyAgreementsPage />} />
        <Route path="timeline" element={<TimelinePage />} />
        <Route path="budget" element={<BudgetPage />} />
        <Route path="registry" element={<RegistryPage />} />
        <Route path="vow-studio" element={<VowStudioPage />} />
        <Route path="seating" element={<SeatingChartPage />} />
        <Route path="reception-mode" element={<ReceptionModePage />} />
        <Route path="guest-messages" element={<GuestMessagesPage />} />
        <Route path="videos" element={<VideosPage />} />
        <Route path="invites" element={<AnimatedInvitesPage />} />
        <Route path="ai-planner" element={<AiPlannerPage />} />
        <Route path="sessions" element={<SessionsPage />} />
        <Route path="live" element={<GoLivePage />} />
        <Route path="referrals" element={<ReferralsPage />} />
        <Route path="vendor-profile" element={<VendorProfileEditor />} />
        <Route path="vendor-inbox" element={<VendorInboxPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="club" element={<MyClubPage />} />
        <Route path="concierge" element={<ConciergePage />} />
        <Route path="quiz" element={<QuizPage />} />
        <Route path="love-story" element={<LoveStoryPage />} />
        <Route path="anniversary" element={<AnniversaryPage />} />
        <Route path="album" element={<AlbumPage />} />
        <Route path="locked-in" element={<LockedInHub />} />
        <Route path="locked-in/answer" element={<LockedInAnswerPage />} />
        <Route path="locked-in/match" element={<LockedInMatchPage />} />
        <Route path="locked-in/games/new" element={<LockedInGameCreate />} />
        <Route path="locked-in/games/:id" element={<LockedInGameHost />} />
        <Route path="compatibility" element={<CompatibilityPage />} />
        <Route path="announcements" element={<AnnouncementsManagerPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
      </WaiverGate>
      <TourGuide />
      </motion.div>
      <MobileTabBar />
      <InstallPrompt />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <AuthProvider>
          <BrowserRouter>
            <SiteTags />
            <AppRouter />
          </BrowserRouter>
          <Toaster richColors position="top-center" />
        </AuthProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
